
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Area,
  AreaChart
} from "recharts";
import {
  TrendingUp,
  DollarSign,
  Users,
  CreditCard,
  AlertTriangle,
  Send,
  Clock,
  Loader2
} from "lucide-react";
import { useState, useRef, useEffect } from "react"; 

import { useSchoolFinanceData } from "./SchoolFinanceLogic";
import ClassFinanceDetails from "./ClassFinanceDetails"; 

const FinanceDashboard = () => {
  // Use the shared finance logic hook
  const { outstandingByClass, calculatingClasses, paymentProviders, schoolID } = useSchoolFinanceData(); 
  const [selectedClass, setSelectedClass] = useState<string | null>(null); 
  const scrollRef = useRef<boolean>(false);

  // Scroll back to section when returning from details view
  useEffect(() => {
    if (!selectedClass && scrollRef.current) {
        setTimeout(() => {
            const element = document.getElementById('outstanding-fees-section');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
        scrollRef.current = false;
    }
  }, [selectedClass]);

  // Conditional rendering for ClassFinanceDetails
  if (selectedClass && schoolID) {
    return (
      <ClassFinanceDetails
        className={selectedClass}
        schoolID={schoolID}
        paymentProviders={paymentProviders}
        onBack={() => setSelectedClass(null)}
      />
    );
  }

  // Sample data for charts (keeping these for now until we have charts logic)
  const monthlyRevenue = [
    { month: "Jan", revenue: 120000, expenses: 85000, netIncome: 35000 },
    { month: "Feb", revenue: 135000, expenses: 92000, netIncome: 43000 },
    { month: "Mar", revenue: 127450, expenses: 89300, netIncome: 38150 },
    { month: "Apr", revenue: 142000, expenses: 95000, netIncome: 47000 },
    { month: "May", revenue: 138000, expenses: 91000, netIncome: 47000 },
    { month: "Jun", revenue: 145000, expenses: 97000, netIncome: 48000 }
  ];

  const feeCategories = [
    { name: "Tuition", value: 65000, color: "#3B82F6" },
    { name: "Boarding", value: 35000, color: "#10B981" },
    { name: "Transport", value: 15000, color: "#F59E0B" },
    { name: "Activities", value: 8000, color: "#EF4444" },
    { name: "Uniforms", value: 4450, color: "#8B5CF6" }
  ];

  const expenseBreakdown = [
    { category: "Salaries", amount: 45000, percentage: 50.4 },
    { category: "Utilities", amount: 12000, percentage: 13.4 },
    { category: "Supplies", amount: 18000, percentage: 20.2 },
    { category: "Maintenance", amount: 8000, percentage: 9.0 },
    { category: "Other", amount: 6300, percentage: 7.0 }
  ];

  const paymentMethods = [
    { method: "Bank Transfer", count: 156, amount: 87450 },
    { method: "Mobile Money", count: 89, amount: 28500 },
    { method: "Cash", count: 45, amount: 11500 },
    { method: "Card", count: 23, amount: 8200 }
  ];

  const chartConfig = {
    revenue: { label: "Revenue", color: "#3B82F6" },
    expenses: { label: "Expenses", color: "#EF4444" },
    netIncome: { label: "Net Income", color: "#10B981" }
  };

  return (
    <div className="space-y-6">
      {/* Key Performance Indicators */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Collection Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">84.6%</div>
            <p className="text-xs text-muted-foreground">+2.1% from last term</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Payment Time</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">12 days</div>
            <p className="text-xs text-muted-foreground">-3 days improvement</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Students Paid</CardTitle>
            <Users className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">1,043</div>
            <p className="text-xs text-muted-foreground">of 1,234 total students</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit Margin</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">29.9%</div>
            <p className="text-xs text-muted-foreground">+4.2% from last term</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue vs Expenses Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Revenue vs Expenses
            </CardTitle>
            <CardDescription>Monthly financial performance overview</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" fontSize={12} />
                  <YAxis fontSize={12} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    stackId="1" 
                    stroke="#3B82F6" 
                    fill="#3B82F6" 
                    fillOpacity={0.6}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="expenses" 
                    stackId="2" 
                    stroke="#EF4444" 
                    fill="#EF4444" 
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              Fee Categories Breakdown
            </CardTitle>
            <CardDescription>Revenue distribution by fee type</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={feeCategories}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {feeCategories.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {feeCategories.map((category, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: category.color }}
                  />
                  <span className="text-xs">{category.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expense Breakdown and Payment Methods */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Expense Breakdown</CardTitle>
            <CardDescription>Current term expense distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {expenseBreakdown.map((expense, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="font-medium text-sm">{expense.category}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">${expense.amount.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">{expense.percentage}%</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment Methods</CardTitle>
            <CardDescription>Transaction distribution by payment type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {paymentMethods.map((method, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{method.method}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">${method.amount.toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">{method.count} transactions</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Outstanding Fees by Class (Real Data) */}
      {/* Outstanding Fees by Class (Real Data) */}
      <Card id="outstanding-fees-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            Outstanding Fees by Class
          </CardTitle>
          <CardDescription>Real-time aggregation from {outstandingByClass.length} classes</CardDescription>
        </CardHeader>
        <CardContent>
          {calculatingClasses ? (
             <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Calculating class balances...
             </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {outstandingByClass.map((item, index) => (
                <div 
                  key={index} 
                  onClick={() => {
                      setSelectedClass(item.name);
                      scrollRef.current = true;
                  }}
                  className="flex items-center justify-between p-4 bg-muted/20 rounded-xl hover:bg-muted/40 transition-all duration-300 border border-transparent hover:border-border/50 group cursor-pointer"
                >
                  <div>
                    <div className="font-semibold text-base flex items-center gap-2">
                      {item.name}
                      {item.missingData > 0 && (
                        <div className="relative group">
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-black text-white text-xs rounded z-10">
                            {item.missingData} students have missing admission dates.
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">{item.students} students</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">KES {item.amount.toLocaleString()}</div>
                    {item.amount > 0 ? (
                      <Badge variant="destructive">Overdue</Badge>
                    ) : item.missingData > 0 ? (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                        Check Data
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-green-600 border-green-600 bg-green-50 dark:bg-green-900/20">
                        Good
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
              {outstandingByClass.length === 0 && (
                <div className="col-span-full py-4 text-center text-muted-foreground">
                  No outstanding fees found.
                </div>
              )}
            </div>
          )}
          <div className="mt-6 pt-6 border-t border-dashed border-border/60">
            <Button className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/20 transition-all duration-300">
              <Send className="w-4 h-4 mr-2" />
              Send Payment Reminders
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinanceDashboard;